import React, { useEffect, useState, useMemo } from "react";
import { DataGrid } from "../DataGrid";
import {
    GridColumnVisibilityModel,
    useGridApiRef,
    GridFilterModel,
    gridFilteredSortedRowEntriesSelector,
    GridFilterItem,
} from "@mui/x-data-grid-pro";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { Trade, SearchParameters } from "../../types";
import { FullScreenBox } from "./FullScreenBox";
import { TradeColDef, useColumnDefinitions } from "./columnDefinitions";
import { TradeWithId, addIdsToTrades, tradesToCsv, downloadCsv } from "./utils";
import { DataGridToolbar, TradeStateFilter } from "./DataGridToolbar";
import { putToClipboard } from "../../clipboard";

interface Props {
    trades: Trade[];
    selectedTrades?: Trade[];
    onSelectionChange?: (selectedTrades: Trade[]) => void;
    onFilterChange?: (filteredTrades: Trade[]) => void;
    searchParameters: SearchParameters | null;
}

const COPY_LIMIT = 10000;

export const TradesGrid: React.FC<Props> = ({
    trades,
    selectedTrades = [],
    onSelectionChange = () => null,
    searchParameters,
    onFilterChange = () => null,
}) => {
    const apiRef = useGridApiRef();

    const columns = useColumnDefinitions(trades);
    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(
        columns.filter((col) => col.hideByDefault).reduce((obj, col) => ({ ...obj, [col.field]: false }), {})
    );
    const [filterModel, setFilterModel] = useState<GridFilterModel>();
    const [clipboardWarningOpen, setClipboardWarningOpen] = useState<boolean>(false);
    const [selectedTradeStates, setSelectedTradeStates] = React.useState<TradeStateFilter[]>(["current"]);

    // Trade state menu open state is lifted up to here to allow updating the table when clicking on any dropdown option (instead of waiting for the menu to close)
    const [tradeStateMenuOpen, setTradeStateMenuOpen] = useState<boolean>(false);

    const tradesWithId: TradeWithId[] = useMemo(() => addIdsToTrades(trades), [trades]);
    const filteredTradesWithId: TradeWithId[] = useMemo(() => {
        const includeAll = selectedTradeStates.includes("all");
        const includeDeleted = selectedTradeStates.includes("deleted");

        if (includeAll || includeDeleted) {
            return tradesWithId;
        }
        return tradesWithId.filter((trade) => !trade.isDeletedTrade);
    }, [tradesWithId, selectedTradeStates]);
    const selectedTradesWithId: TradeWithId[] = useMemo(() => addIdsToTrades(selectedTrades), [selectedTrades]);

    const isOwnSet =
        filterModel?.linkOperator === "and" &&
        filterModel?.items?.some((item) => item.columnField === "isPrivateTrade" && item.value === "true");

    const setIsOwnFilter = (filterIsOwn: boolean) => {
        if (filterIsOwn && !isOwnSet) {
            setFilterModel(
                (_model) =>
                    ({
                        ..._model,
                        linkOperator: "and",
                        items: [
                            ...(_model?.items.filter((item) => item.columnField !== "isPrivateTrade") || []),
                            { columnField: "isPrivateTrade", operatorValue: "is", value: "true" },
                        ],
                    } as GridFilterModel)
            );
        } else if (!filterIsOwn) {
            setFilterModel(
                (_model) =>
                    ({
                        ..._model,
                        items: _model?.items.filter((item) => item.columnField !== "isPrivateTrade"),
                    } as GridFilterModel)
            );
        }
        updateFilteredTrades();
    };

    const setTradeStatesFilter = (tradeStates: TradeStateFilter[]) => {
        setSelectedTradeStates(tradeStates);
        if(tradeStates.includes("all") || tradeStates.length === 2){
            setFilterModel(
                (_model) =>
                    ({
                        ..._model,
                        linkOperator: "and",
                        items: [
                            ...(_model?.items.filter((item) => item.columnField !== "isDeletedTrade") || []),
                            { columnField: "isDeletedTrade", operatorValue: "is", value: "any" },
                        ],
                    } as GridFilterModel)
            );
        }
        else if (tradeStates.includes("deleted")){
            setFilterModel(
                (_model) =>
                    ({
                        ..._model,
                        linkOperator: "and",
                        items: [
                            ...(_model?.items.filter((item) => item.columnField !== "isDeletedTrade") || []),
                            { columnField: "isDeletedTrade", operatorValue: "is", value: "true" },
                        ],
                    } as GridFilterModel)
            );
        }else{
            setFilterModel(
                (_model) =>
                    ({
                        ..._model,
                        items: _model?.items.filter((item) => item.columnField !== "isDeletedTrade"),
                    } as GridFilterModel)
            );
        }
        updateFilteredTrades();
    };

    const getVisibleTrades = () => Array.from(apiRef.current.getVisibleRowModels().values()) as Trade[];

    const pushToClipboard = (_trades: Trade[], withHeader = true, htmlNewLine = false) => {
        if(_trades.length > COPY_LIMIT){
            setClipboardWarningOpen(true);
        }
        const tradeFields = tradesToCsv(
            _trades.slice(0, COPY_LIMIT),
            apiRef.current.getVisibleColumns() as TradeColDef[],
            withHeader,
            "\t",
            htmlNewLine ? "<br />" : "\n"
        );
        putToClipboard(tradeFields);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const charCode = String.fromCharCode(event.which).toLowerCase();
        if ((event.ctrlKey || event.metaKey) && charCode === "c") {
            event.preventDefault();
            pushToClipboard(selectedTrades || trades, event.shiftKey, true);
        }
    };

    const downloadTradesCsv = (_trades: Trade[]) => {
        const fileName =
            `${searchParameters?.from?.toISOString()}-${searchParameters?.until?.toISOString()}-` +
            `${searchParameters?.instrument?.id}:${searchParameters?.sequence?.id}:${searchParameters?.sequenceItem?.id}:` +
            `${searchParameters?.secondSequenceItem?.id || "0"}:${searchParameters?.contractType}` +
            `.csv`;
        const csvData = tradesToCsv(_trades, apiRef.current.getAllColumns() as TradeColDef[]);

        downloadCsv(csvData, fileName);
    };

    const downloadFilteredTrades = () => {
        downloadTradesCsv(getVisibleTrades());
    };

    const copyFilteredTrades = () => {
        pushToClipboard(getVisibleTrades());
    };

    const updateFilteredTrades = () => {
        return setTimeout(() => {
            const filteredRows = gridFilteredSortedRowEntriesSelector(apiRef) || [];
            const filteredTradeIds = filteredRows.map((row) => row.id as string);
            const filteredTrades = filteredRows.map((row) => row.model as Trade);

            onFilterChange(filteredTrades);

            if (!selectedTradesWithId.every((trade) => filteredTradeIds.includes(trade.id))) {
                onSelectionChange(selectedTradesWithId.filter((trade) => filteredTradeIds.includes(trade.id)));
            }
        }, 100);    // Delay to wait for the table contents to update...
    };

    const handleDeletedTradeFilterChange = (filters: GridFilterItem[]) => {
        if (filters.length === 0) {
            setSelectedTradeStates(["current"]);
            return;
        }
        const filterValue = filters[0].value;
        const nextStates: TradeStateFilter[] = filterValue === "any" ? ["all"] : filterValue === true ? ["deleted"] : ["current"];
        setSelectedTradeStates(nextStates);
    }

    useEffect(() => {
        const timeout = updateFilteredTrades();
        return () => clearTimeout(timeout);
    }, [selectedTradesWithId.length, filteredTradesWithId.length, selectedTradeStates.join(":")]);

    const handleFilterModelChange = (filterModel: GridFilterModel) => {
        setFilterModel(filterModel);
        handleDeletedTradeFilterChange(filterModel.items.filter((item) => item.columnField === "isDeletedTrade") || []);
        updateFilteredTrades();
    };

    return (
        <FullScreenBox onKeyDown={handleKeyDown}>
            <DataGrid
                sx={{ width: "100%" }}
                columns={columns}
                density="compact"
                hideFooter
                rowHeight={32}
                rows={filteredTradesWithId}
                getRowClassName={(params) => (params.row.aggressorCompanyId !== undefined ? "trades-grid-private" : "")}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
                onSelectionModelChange={(newSelectionModel) => {
                    onSelectionChange(filteredTradesWithId.filter((t) => newSelectionModel.includes(t.id)));
                }}
                selectionModel={selectedTradesWithId.map((t) => t.id)}
                components={{
                    Toolbar: DataGridToolbar,
                }}
                componentsProps={{
                    toolbar: {
                        isOwnSet,
                        onIsOwnChange: setIsOwnFilter,
                        onCopy: copyFilteredTrades,
                        onDownload: downloadFilteredTrades,
                        copyDisabled: filteredTradesWithId.length <= 0,
                        selectedTradeStates,
                        onTradeStatesChange: setTradeStatesFilter,
                        setTradeStateMenuOpen,
                        tradeStateMenuOpen,
                    } as React.ComponentProps<typeof DataGridToolbar>,
                }}
                apiRef={apiRef}
                filterModel={filterModel}
                onFilterModelChange={handleFilterModelChange}
            />
            <Snackbar
                open={clipboardWarningOpen}
                autoHideDuration={6000}
                onClose={() => setClipboardWarningOpen(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert onClose={() => setClipboardWarningOpen(false)} severity="error" variant="filled">
                    Warning: copying to clipboard is limited to {COPY_LIMIT} trades.
                    <br />
                    Please download the data as CSV instead.
                </Alert>
            </Snackbar>
        </FullScreenBox>
    );
};
